import { DateTime } from 'luxon';
import { supabase } from '../supabase';

export interface StaffReportUserNotes {
  user_id: string;
  notes: string;
  strike1: string;
  strike2: string;
  strike3: string;
  lastUpdated?: DateTime;
  lastUpdatedBy?: string;
}

export interface OverwatchRequest {
  user_id: string;
  shiftPattern: string;
  model_id: string;
  notes: string;
  assignedOverwatcher?: string;
  dateTime: DateTime | null;
  completed: boolean;
  completedAt?: DateTime | null;
  lastUpdated?: DateTime;
  lastUpdatedBy?: string;
}

// Internal Supabase types
interface StaffReportNotesRow {
  id: string;
  user_id: string;
  notes: string;
  strike1: string;
  strike2: string;
  strike3: string;
  last_updated_by: string | null;
  created_at: string;
  updated_at: string;
}

interface StaffReportOverwatchRow {
  id: string;
  user_id: string;
  shift_pattern: string;
  model_id: string;
  notes: string;
  assigned_overwatcher: string | null;
  date_time: string | null;
  completed: boolean;
  completed_at: string | null;
  last_updated_by: string | null;
  created_at: string;
  updated_at: string;
}


// Callback types
export type StaffReportNotesListener = (user_id: string, userData: StaffReportUserNotes | null) => void;
export type OverwatchRequestListener = (requestId: string, requestData: OverwatchRequest | null) => void;

// Helper functions to convert between formats
const rowToUserNotes = (row: StaffReportNotesRow): StaffReportUserNotes => ({
  user_id: row.user_id,
  notes: row.notes,
  strike1: row.strike1,
  strike2: row.strike2,
  strike3: row.strike3,
  lastUpdated: DateTime.fromISO(row.updated_at, { zone: 'UTC' }),
  lastUpdatedBy: row.last_updated_by || undefined
});

const rowToOverwatchRequest = (row: StaffReportOverwatchRow): OverwatchRequest => ({
  user_id: row.user_id,
  shiftPattern: row.shift_pattern,
  model_id: row.model_id,
  notes: row.notes,
  assignedOverwatcher: row.assigned_overwatcher || undefined,
  dateTime: row.date_time ? DateTime.fromISO(row.date_time, { zone: 'UTC' }) : null,
  completed: row.completed,
  completedAt: row.completed_at ? DateTime.fromISO(row.completed_at, { zone: 'UTC' }) : null,
  lastUpdated: DateTime.fromISO(row.updated_at, { zone: 'UTC' }),
  lastUpdatedBy: row.last_updated_by || undefined
});


const StaffReportNotesService = {
  /**
   * Get notes and strikes data for a specific user
   */
  async getUserNotesData(user_id: string): Promise<StaffReportUserNotes | null> {
    try {
      const { data, error } = await supabase
        .from('staff_report_notes')
        .select('*')
        .eq('user_id', user_id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows found
          return null;
        }
        throw error;
      }

      return rowToUserNotes(data);
    } catch (error) {
      console.error('Error getting user notes data:', error);
      throw error;
    }
  },

  /**
   * Get notes and strikes data for multiple users
   */
  async getMultipleUsersNotesData(user_ids: string[]): Promise<Map<string, StaffReportUserNotes>> {
    try {
      const userNotesMap = new Map<string, StaffReportUserNotes>();

      if (user_ids.length === 0) {
        return userNotesMap;
      }

      const BATCH_SIZE = 500;
      const batches: string[][] = [];

      for (let i = 0; i < user_ids.length; i += BATCH_SIZE) {
        batches.push(user_ids.slice(i, i + BATCH_SIZE));
      }

      const results = await Promise.all(
        batches.map(async (batch) => {
          const { data, error } = await supabase
            .from('staff_report_notes')
            .select('*')
            .in('user_id', batch);

          if (error) throw error;
          return data || [];
        })
      );

      results.flat().forEach(row => {
        userNotesMap.set(row.user_id, rowToUserNotes(row));
      });

      return userNotesMap;
    } catch (error) {
      console.error('Error getting multiple users notes data:', error);
      throw error;
    }
  },

  /**
   * Update a specific user's notes and strikes data
   */
  async updateUserNotesData(
    user_id: string,
    updates: {
      notes?: string;
      strike1?: string;
      strike2?: string;
      strike3?: string;
    },
    updatedBy: string
  ): Promise<void> {
    try {
      const updateData = {
        ...updates,
        last_updated_by: updatedBy
      };

      const { error } = await supabase
        .from('staff_report_notes')
        .upsert({
          user_id,
          notes: '',
          strike1: '',
          strike2: '',
          strike3: '',
          ...updateData
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error updating user notes data:', error);
      throw error;
    }
  },

  /**
   * Update multiple users at once
   */
  async updateMultipleUsersNotesData(
    userUpdates: Array<{
      user_id: string;
      updates: {
        notes?: string;
        strike1?: string;
        strike2?: string;
        strike3?: string;
      };
    }>,
    updatedBy: string
  ): Promise<void> {
    try {
      // First get existing data for users being updated
      const userIds = userUpdates.map(u => u.user_id);
      const existingData = await this.getMultipleUsersNotesData(userIds);

      // Prepare upsert data
      const upsertData = userUpdates.map(({ user_id, updates }) => {
        const existing = existingData.get(user_id);
        return {
          user_id,
          notes: updates.notes ?? existing?.notes ?? '',
          strike1: updates.strike1 ?? existing?.strike1 ?? '',
          strike2: updates.strike2 ?? existing?.strike2 ?? '',
          strike3: updates.strike3 ?? existing?.strike3 ?? '',
          last_updated_by: updatedBy
        };
      });

      const { error } = await supabase
        .from('staff_report_notes')
        .upsert(upsertData, {
          onConflict: 'user_id'
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error updating multiple users notes data:', error);
      throw error;
    }
  },

  /**
   * Listen for changes to a specific user's notes
   */
  listenToUserNotes(
    user_id: string,
    callback: StaffReportNotesListener
  ): () => void {
    const subscription = supabase
      .channel(`staff_report_notes:${user_id}`, { config: { private: true } })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'staff_report_notes',
        filter: `user_id=eq.${user_id}`
      }, (payload) => {
        if (payload.eventType === 'DELETE') {
          callback(user_id, null);
        } else {
          callback(user_id, rowToUserNotes(payload.new as StaffReportNotesRow));
        }
      })
      .subscribe(
        (status, err) => {
          console.log('Staff report notes subscription status:', status);
          if (status === 'CHANNEL_ERROR') {
            console.error('Subscription error details:', err);
          }
        }
      );

    return () => {
      subscription.unsubscribe();
    };
  },

  /**
   * Listen for changes to multiple users' notes
   */
  listenToMultipleUsersNotes(
    user_ids: string[],
    callback: StaffReportNotesListener
  ): () => void {
    const userIdsSet = new Set(user_ids);

    const subscription = supabase
      .channel('staff_report_notes_multiple', { config: { private: true } })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'staff_report_notes'
      }, (payload) => {
        const userId = payload.eventType === 'DELETE'
          ? (payload.old as StaffReportNotesRow).user_id
          : (payload.new as StaffReportNotesRow).user_id;

        // Only call callback if this user is in our list
        if (userIdsSet.has(userId)) {
          if (payload.eventType === 'DELETE') {
            callback(userId, null);
          } else {
            callback(userId, rowToUserNotes(payload.new as StaffReportNotesRow));
          }
        }
      })
      .subscribe(
        (status, err) => {
          console.log('Multiple users notes subscription status:', status);
          if (status === 'CHANNEL_ERROR') {
            console.error('Multiple users subscription error details:', err);
          }
        }
      );

    return () => {
      subscription.unsubscribe();
    };
  },

  /**
   * Delete a user's notes data
   */
  async deleteUserNotesData(user_id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('staff_report_notes')
        .delete()
        .eq('user_id', user_id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting user notes data:', error);
      throw error;
    }
  },

  /**
   * Create a new Overwatch request
   */
  async createOverwatchRequest(
    user_id: string,
    shiftPattern: string,
    model_id: string,
    notes: string,
    createdBy: string
  ): Promise<string> {
    try {
      const { data, error } = await supabase
        .from('staff_report_overwatch')
        .insert({
          user_id,
          shift_pattern: shiftPattern,
          model_id,
          notes,
          completed: false,
          last_updated_by: createdBy
        })
        .select('id')
        .single();

      if (error) throw error;

      return data.id;
    } catch (error) {
      console.error('Error creating overwatch request:', error);
      throw error;
    }
  },

  /**
   * Get all Overwatch requests
   */
  async getAllOverwatchRequests(): Promise<Map<string, OverwatchRequest>> {
    try {
      const { data, error } = await supabase
        .from('staff_report_overwatch')
        .select('*');

      if (error) throw error;

      const requestsMap = new Map<string, OverwatchRequest>();
      data?.forEach(row => {
        requestsMap.set(row.id, rowToOverwatchRequest(row));
      });

      return requestsMap;
    } catch (error) {
      console.error('Error getting overwatch requests:', error);
      throw error;
    }
  },

  /**
   * Update an existing Overwatch request
   */
  async updateOverwatchRequest(
    requestId: string,
    updates: {
      assignedOverwatcher?: string;
      completed?: boolean;
      notes?: string;
      dateTime?: DateTime | null;
      completedAt?: DateTime | null;
    },
    updatedBy: string
  ): Promise<void> {
    try {
      const updateData: any = {
        last_updated_by: updatedBy
      };

      if (updates.assignedOverwatcher !== undefined) {
        updateData.assigned_overwatcher = updates.assignedOverwatcher || null;
      }
      if (updates.completed !== undefined) {
        updateData.completed = updates.completed;
      }
      if (updates.notes !== undefined) {
        updateData.notes = updates.notes;
      }
      if (updates.dateTime !== undefined) {
        updateData.date_time = updates.dateTime ? updates.dateTime.toISO() : null;
      }
      if (updates.completedAt !== undefined) {
        updateData.completed_at = updates.completedAt ? updates.completedAt.toISO() : null;
      }

      const { error } = await supabase
        .from('staff_report_overwatch')
        .update(updateData)
        .eq('id', requestId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating overwatch request:', error);
      throw error;
    }
  },

  /**
   * Listen for changes to all Overwatch requests
   */
  listenToOverwatchRequests(
    callback: (requests: Map<string, OverwatchRequest>) => void
  ): () => void {
    // Initial load
    this.getAllOverwatchRequests().then(callback);

    const subscription = supabase
      .channel('staff_report_overwatch', { config: { private: true } })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'staff_report_overwatch'
      }, () => {
        // Reload all data on any change
        this.getAllOverwatchRequests().then(callback);
      })
      .subscribe(
        (status, err) => {
          console.log('Overwatch requests subscription status:', status);
          if (status === 'CHANNEL_ERROR') {
            console.error('Subscription error details:', err);
          }
        }
      );

    return () => {
      subscription.unsubscribe();
    };
  },

  /**
   * Delete an Overwatch request
   */
  async deleteOverwatchRequest(requestId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('staff_report_overwatch')
        .delete()
        .eq('id', requestId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting overwatch request:', error);
      throw error;
    }
  },

};

export default StaffReportNotesService;